import { BlueskySettingKey } from '$extensionSrc/recommendations/recommendationSettingsKeys'
import { BlueskyRecommendationKey, StageType } from '$extensionSrc/utils/enums'
import { wait } from '$extensionSrc/utils/promises'

import { AUTOMATION_STAGE_STATUSES } from '../genericAutomationStageStatuses'
import * as blueskyUrls from './blueskyUrls'
import { StageDef } from '../automationMachineInterfaces'
import {
  getIsFulfilledFnFromKey,
  defaultCantUndoFn,
  getCurrentValueFnFromKey,
  getErrorIsDisabledAndReasonFn,
} from '../recommendationCardDisplayUtils'
import { getElement, shouldUpdateSetting } from '../utils'
import { getCaseInsensitiveXPathTextSelector } from '../xpath'

type SettingValue = 'everyone' | 'users i follow' | 'no one'

const TARGET_VALUE: SettingValue = 'users i follow'

function asStrictOrStricterThan(a: SettingValue | string, b: SettingValue) {
  // Lower values are stricter
  function _getRank(x: SettingValue | string) {
    if (x === 'no one') {
      return 1
    } else if (x === 'users i follow') {
      return 2
    } else if (x === 'everyone') {
      return 3
    }
    return 4
  }

  return _getRank(a) <= _getRank(b)
}

// Settings property functions
export const getCurrentValueFn = (key) =>
  getCurrentValueFnFromKey(key, (value) => value)

export const getIsFulfilledFn = (key) =>
  getIsFulfilledFnFromKey(
    key,
    (value) =>
      value && asStrictOrStricterThan(value?.toLowerCase(), TARGET_VALUE),
  )

export const getCantUndoFn = () => defaultCantUndoFn

export function getIsDisabledFn(settingsKey: string) {
  const fn = getErrorIsDisabledAndReasonFn(settingsKey)
  return (automationErrors) => fn(automationErrors).isDisabled
}
export function getIsDisabledReasonFn(settingsKey: string) {
  const fn = getErrorIsDisabledAndReasonFn(settingsKey)
  return (automationErrors) => fn(automationErrors).isDisabledReason
}

enum StageKey {
  GATHER_SETTING_ALLOW_MESSAGES_FROM = 'GATHER_SETTING_ALLOW_MESSAGES_FROM',
  UPDATE_SETTING_ALLOW_MESSAGES_FROM = 'UPDATE_SETTING_ALLOW_MESSAGES_FROM',
  UNDO_SETTING_ALLOW_MESSAGES_FROM = 'UNDO_SETTING_ALLOW_MESSAGES_FROM',
}

async function getAllowMessagesFromRadioButtons(): Promise<HTMLElement[]> {
  const allowMessagesFrom = await getElement<HTMLElement>(
    document,
    `//*[${getCaseInsensitiveXPathTextSelector('aria-label', 'allow new messages from')}` +
      ' and @role="radiogroup"]',
  )

  return Array.from(allowMessagesFrom.querySelectorAll('div[role="radio"]'))
}

async function getAllowMessagesFromSettings(): Promise<Record<string, string>> {
  const messageSettings = {}

  const radioButtons = await getAllowMessagesFromRadioButtons()
  radioButtons?.forEach((radioButton) => {
    messageSettings[radioButton.ariaLabel] = radioButton.ariaChecked
  })

  return messageSettings
}

async function clickSettingRowByLabel(
  label: string,
  isDry: boolean,
): Promise<void> {
  const radioButtons = await getAllowMessagesFromRadioButtons()

  const targetSetting = radioButtons.find(
    (radioButton) =>
      radioButton.ariaLabel.toLowerCase() === label.toLowerCase(),
  )

  if (targetSetting && !isDry) {
    targetSetting.click()
  }
}

export const gatherStageDefs: StageDef[] = [
  {
    type: StageType.ACTION,
    key: StageKey.GATHER_SETTING_ALLOW_MESSAGES_FROM,
    status: AUTOMATION_STAGE_STATUSES.GATHERING_SETTINGS,
    url: blueskyUrls.messagesSettingsUrl,
    action: async (machineContext, userContext) => {
      const { updateStoredInitialValues } = userContext

      // The user's setting value takes a moment to load. Take a pause before reading value.
      await wait(500)

      const allowMessagesFromSettings = await getAllowMessagesFromSettings()
      const selectedSetting = Object.keys(allowMessagesFromSettings).find(
        (key) => allowMessagesFromSettings[key] === 'true',
      )
      if (selectedSetting) {
        await updateStoredInitialValues(
          {
            [BlueskySettingKey.ALLOW_MESSAGES]: selectedSetting,
          },
          BlueskyRecommendationKey.BLUESKY_MESSAGES,
        )
      }
    },
  },
]
export const updateStageDefs: StageDef[] = [
  {
    type: StageType.ACTION,
    key: StageKey.UPDATE_SETTING_ALLOW_MESSAGES_FROM,
    status: AUTOMATION_STAGE_STATUSES.UPDATING_SETTINGS,
    url: blueskyUrls.messagesSettingsUrl,
    action: async (machineContext, userContext) => {
      const { initialValues, isDry, settingsSelected } = userContext

      if (
        shouldUpdateSetting(
          settingsSelected,
          initialValues,
          BlueskySettingKey.ALLOW_MESSAGES,
          getIsFulfilledFn,
        )
      ) {
        await clickSettingRowByLabel(TARGET_VALUE, isDry)
      }
    },
  },
]

export const undoStageDefs: StageDef[] = [
  {
    type: StageType.ACTION,
    key: StageKey.UNDO_SETTING_ALLOW_MESSAGES_FROM,
    status: AUTOMATION_STAGE_STATUSES.UNDOING_SETTINGS,
    url: blueskyUrls.messagesSettingsUrl,
    action: async (machineContext, userContext) => {
      const { initialValues, settingsSelected, isDry } = userContext

      if (
        shouldUpdateSetting(
          settingsSelected,
          initialValues,
          BlueskySettingKey.ALLOW_MESSAGES,
          getIsFulfilledFn,
        )
      ) {
        await clickSettingRowByLabel(
          initialValues[BlueskySettingKey.ALLOW_MESSAGES],
          isDry,
        )
      }
    },
  },
]
